(function (scope, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    scope.ArohaNativeBoards = api;
    const init = () => document.querySelectorAll('[data-aa-native-boards]').forEach(root => api.mount(root));
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
    document.addEventListener('shopify:section:load', init);
  }
})(typeof window === 'undefined' ? globalThis : window, function () {
  'use strict';
  const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
  const norm = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const contextKeys = ['country', 'currency', 'themeId', 'collectionId'];
  function allowedMarket(context) {
    return context?.country === 'NZ' && context.currency === 'NZD' || context?.country === 'AU' && context.currency === 'AUD';
  }
  function assertContext(expected, fresh, cart, runtime = {}) {
    if (!allowedMarket(expected) || !fresh || contextKeys.some(key => String(expected[key] || '') !== String(fresh[key] || '')) || cart?.currency !== expected.currency || runtime.currency && runtime.currency !== expected.currency || runtime.themeId && String(runtime.themeId) !== String(expected.themeId)) {
      throw Error('Your market or preview changed. Reload before ordering.');
    }
  }
  function validProduct(product) {
    return product?.metadataVersion === 1 && product.sizeOption === 'Size' && product.optionCount === 1 &&
      product.variants?.length === 3 && new Set(product.variants.map(v => v.size)).size === 3 && new Set(product.variants.map(v => v.sizeRole)).size === 3 && product.variants.every(v => ['small', 'medium', 'large'].includes(v.sizeRole)) &&
      ['functional', 'display'].includes(product.use) && typeof product.useLabel === 'string' && product.useLabel.length > 0 && typeof product.name === 'string' && product.name.length > 0 &&
      !!product.designKey && !!product.artwork?.id && !!product.artwork?.url;
  }
  function validVariant(variant) {
    return !!variant?.id && typeof variant.size === 'string' && Number.isSafeInteger(variant.price) && variant.price > 0 &&
      Array.isArray(variant.dimensions) && variant.dimensions.length === 3 && variant.dimensions.every(n => typeof n === 'number' && Number.isFinite(n) && n > 0) &&
      ['small', 'medium', 'large'].includes(variant.sizeRole) && variant.easels === (variant.sizeRole === 'large' ? 2 : 1);
  }
  function count(value, variant) {
    const n = Number(value), min = variant.rule?.min || 1, step = variant.rule?.increment || 1, max = variant.rule?.max ?? 999;
    if (!Number.isSafeInteger(n) || n < min || n > max || (n - min) % step) throw Error(`Choose a valid quantity (${min}–${max}, increments of ${step}).`);
    return n;
  }
  function index(snapshot) {
    const values = new Map();
    for (const product of snapshot.products || []) {
      for (const variant of product.variants || []) {
        if (values.has(String(variant.id))) throw Error('A variant appeared more than once. Reload before ordering.');
        values.set(String(variant.id), { product, variant });
      }
    }
    return values;
  }
  function signature(product, variant) {
    return JSON.stringify({ productId: String(product.id), designKey: product.designKey, name: product.name, use: product.use, metadataVersion: product.metadataVersion, artwork: product.artwork, media: product.media, caption: product.caption, variant: { id: String(variant.id), size: variant.size, sizeRole: variant.sizeRole, price: variant.price, media: variant.media, dimensions: variant.dimensions, easels: variant.easels, rule: variant.rule } });
  }
  function properties(line, selectionId) {
    const { product, variant } = line;
    const result = { Design: product.name, 'Board size': variant.size, Dimensions: variant.dimensions.join(' x ') + ' cm', 'Included easels': String(variant.easels), 'Intended use': product.useLabel, _aa_selection_id: selectionId, _aa_line_id: line.id };
    if (product.caption) result.Supplied = product.caption;
    return result;
  }
  function recorded(cart, line, selectionId) {
    return (cart.items || []).filter(item => String(item.variant_id ?? item.id) === String(line.variant.id) && item.properties?._aa_selection_id === selectionId && item.properties?._aa_line_id === line.id);
  }
  function filterProducts(products, query = '', use = '', theme = '') {
    // Filtering retains the native collection order. It never introduces a catalogue sort.
    return products.filter(p => (!use || p.use === use) && (!theme || p.theme === theme) && norm([p.name, ...(p.aliases || [])].join(' ')).includes(norm(query.trim())));
  }
  function createController({ snapshot, api, uid, onChange = () => {} }) {
    const catalogue = index(snapshot), selectionId = uid();
    let revision = 0, invalid = false, busy = false, locked = false, lines = [], message = '';
    const state = () => ({ revision, invalid, busy, locked, message, lines: lines.map(line => ({ ...line })), context: { ...snapshot.context } });
    const emit = () => onChange(state());
    function mutable() { if (busy || locked || invalid) throw Error('Review your cart or reload before ordering again.'); }
    function add(variantId, value) {
      mutable();
      const choice = catalogue.get(String(variantId));
      if (!allowedMarket(snapshot.context) || !choice || !validProduct(choice.product) || !validVariant(choice.variant) || !choice.variant.available) throw Error('This board option is not available to order.');
      const existing = lines.find(line => String(line.variant.id) === String(variantId));
      const quantity = count(Number(value) + (existing?.quantity || 0), choice.variant);
      if (existing) existing.quantity = quantity;
      else lines.push({ ...choice, id: uid(), quantity, signature: signature(choice.product, choice.variant) });
      revision++; message = 'Added to your selection.'; emit();
    }
    function update(lineId, value) { mutable(); const line = lines.find(l => l.id === lineId); if (!line) return; line.quantity = count(value, line.variant); revision++; emit(); }
    function remove(lineId) { mutable(); lines = lines.filter(l => l.id !== lineId); revision++; emit(); }
    function invalidate() { invalid = true; revision++; message = 'The page context changed. Reload before ordering.'; emit(); }
    async function submit() {
      mutable(); if (!lines.length) throw Error('Choose at least one board.');
      busy = true; message = 'Checking current options and your cart…'; emit();
      const selected = lines.map(l => ({ ...l })), startedRevision = revision;
      let attempted = false;
      const guard = () => { if (invalid || revision !== startedRevision) throw Error('The board option or market changed. Reload before ordering.'); };
      async function preflight(line) {
        const fresh = await api.snapshot(); guard();
        const cart = await api.cart(); guard();
        assertContext(snapshot.context, fresh.context, cart, api.runtime?.() || {});
        const latest = index(fresh).get(String(line.variant.id));
        if (!latest || !validProduct(latest.product) || !validVariant(latest.variant) || !latest.variant.available || signature(latest.product, latest.variant) !== line.signature) throw Error('The board, photo, size or price changed. Reload before ordering.');
        count(line.quantity, latest.variant);
        if (recorded(cart, line, selectionId).length) throw Error('This board add is already recorded in your cart. Review your cart.');
        return fresh;
      }
      try {
        // Validate the complete batch before the first write, then each line immediately before its one write.
        for (const line of selected) await preflight(line);
        await api.beforeMutation?.(); guard();
        for (const line of selected) {
          await preflight(line); guard();
          message = `Adding ${line.product.name} — ${line.variant.size}…`; emit();
          let response = null, postError = null;
          attempted = true;
          try { response = await api.add({ id: String(line.variant.id), quantity: line.quantity, properties: properties(line, selectionId) }); }
          catch (error) { postError = error; }
          let cart;
          try { cart = await api.cart(); }
          catch { throw Error('The add result is uncertain and the cart could not be checked.'); }
          assertContext(snapshot.context, snapshot.context, cart, api.runtime?.() || {}); guard();
          const items = recorded(cart, line, selectionId), actual = items.reduce((n, item) => n + item.quantity, 0);
          const pricesMatch = items.every(item => item.original_price === line.variant.price);
          if (postError || !response?.ok || actual !== line.quantity || !pricesMatch) throw Error(`Cart review required: ${actual} of ${line.quantity} ${line.product.name} recorded. ${response?.description || 'The add result or price needs checking.'}`);
        }
        const cart = await api.cart(); assertContext(snapshot.context, snapshot.context, cart, api.runtime?.() || {});
        locked = true; lines = []; message = 'Added. Review your cart for Shopify’s final discounts, shipping and total.';
        return { status: 'added', cart };
      } catch (error) {
        if (attempted) locked = true;
        message = error.message + (attempted ? ' No automatic retry was made. Review your cart before adding again.' : '');
        return { status: attempted ? 'review-cart' : 'blocked', message, attempted };
      } finally { busy = false; emit(); }
    }
    return { state, add, update, remove, invalidate, submit };
  }
  function parseSnapshot(doc, sectionId) {
    const roots = [...doc.querySelectorAll('[data-aa-native-boards]')];
    const root = roots.find(r => !sectionId || r.dataset.sectionId === sectionId);
    if (!root) throw Error('The current board section could not be verified.');
    return { context: JSON.parse(root.querySelector('[data-anb-context]').textContent), products: [...root.querySelectorAll('[data-anb-product-json]')].map(el => JSON.parse(el.textContent)) };
  }
  function createDirectController({ snapshot, api, uid, onChange = () => {} }) {
    const copy = snapshot.context.copy || {};
    let active = null, busy = false, locked = false, invalid = false, message = '', variantId = '';
    const state = () => ({ busy, locked, invalid, message, variantId });
    const emit = () => onChange(state());
    async function addToCart(id, value) {
      if (busy || locked || invalid) throw Error(copy.locked || 'Review your cart or reload before ordering again.');
      variantId = String(id); busy = true;
      message = copy.checking || 'Checking current options and your cart.'; emit();
      try {
        active = createController({ snapshot, api, uid, onChange: next => {
          if (next.busy) { message = next.message; emit(); }
        } });
        active.add(id, value);
        const result = await active.submit();
        locked = active.state().locked;
        if (invalid && result.status === 'added') {
          message = (copy.changed || 'Your market or page changed. Reload before ordering.') + ' ' + (copy.locked || 'Review your cart or reload before ordering again.');
          return { status: 'review-cart', message, attempted: true };
        }
        message = result.status === 'added' ? (copy.added || 'Added to cart. Opening your cart.') : result.message;
        return result;
      } catch (error) {
        message = error.message; throw error;
      } finally { busy = false; emit(); }
    }
    function invalidate() {
      invalid = true; active?.invalidate();
      message = copy.changed || 'Your market or page changed. Reload before ordering.'; emit();
    }
    return { state, addToCart, invalidate };
  }
  function sourceURL(href, sectionId) { const url = new URL(href); url.hash = ''; url.searchParams.set('section_id', sectionId); return url.href; }
  function previewLink(target, href, context) {
    const from = new URL(href), to = new URL(target, from);
    if (to.origin === from.origin) {
      const preview = from.searchParams.get('preview_theme_id') || (context.themeRole === 'unpublished' ? context.themeId : '');
      if (preview && /^\d+$/.test(String(preview))) to.searchParams.set('preview_theme_id', preview);
    }
    return to.href;
  }
  function trapDialogTab(dialog, event) {
    if (!dialog.open || event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
    const doc = dialog.ownerDocument;
    const selector = 'a[href],button,input:not([type="hidden"]),select,textarea,summary,[tabindex],[contenteditable="true"]';
    const readStops = () => [...dialog.querySelectorAll(selector)].filter(node => {
      if (node.tabIndex < 0 || node.matches(':disabled') || node.closest('[hidden],[inert]') || !node.getClientRects().length) return false;
      const style = doc.defaultView.getComputedStyle(node);
      return style.visibility !== 'hidden' && style.visibility !== 'collapse';
    }).map((node, order) => ({ node, order })).sort((a, b) => {
      const priority = node => node.tabIndex > 0 ? node.tabIndex : Number.MAX_SAFE_INTEGER;
      return priority(a.node) - priority(b.node) || a.order - b.order;
    }).map(item => item.node);
    const stops = readStops();
    event.preventDefault();
    if (!stops.length) { dialog.focus(); return; }
    const current = stops.indexOf(doc.activeElement);
    const next = current < 0 ? (event.shiftKey ? stops.length - 1 : 0) : (current + (event.shiftKey ? -1 : 1) + stops.length) % stops.length;
    // Normal focus scrolling keeps lower controls reachable in short/mobile viewports.
    stops[next].focus();
    // Blurring an edited quantity can synchronously replace the review rows.
    if (!stops[next].isConnected || !dialog.contains(doc.activeElement)) {
      const fresh = readStops();
      (fresh[Math.min(next, fresh.length - 1)] || dialog).focus();
    }
  }
  function returnFocus(opener, fallback) {
    const target = opener?.isConnected && !opener.disabled ? opener : fallback;
    target?.focus();
  }
  function mount(root, injectedAPI) {
    if (root.dataset.enhanced || typeof HTMLDialogElement === 'undefined') return;
    const sectionId = root.dataset.sectionId, snapshot = parseSnapshot(document, sectionId);
    const doc = root.ownerDocument, context = snapshot.context, artwork = root.querySelector('[data-anb-artwork-dialog]');
    artwork.addEventListener('keydown', event => {
      const close = artwork.querySelector('[data-anb-close]'), body = artwork.querySelector('[data-anb-artwork-body]');
      if (body && [close, artwork, body].includes(event.target)) {
        const page = Math.max(1, body.clientHeight * .85);
        if (event.key === 'PageDown' || event.key === 'PageUp') { event.preventDefault(); body.scrollBy({top:event.key === 'PageDown' ? page : -page, behavior:'auto'}); }
        else if (event.key === 'Home' || event.key === 'End') { event.preventDefault(); body.scrollTo({top:event.key === 'Home' ? 0 : body.scrollHeight, behavior:'auto'}); }
      }
      trapDialogTab(artwork, event);
    });
    const status = root.querySelector('[data-anb-status]');
    let imageOpener = null, activeCard = null;
    const uid = () => crypto.randomUUID();
    const route = path => new URL((window.Shopify?.routes?.root || '/') + path, location.origin).href;
    const api = injectedAPI || {
      async snapshot() { const r = await fetch(sourceURL(location.href, sectionId), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } }); if (!r.ok) throw Error('Current options could not be checked.'); return parseSnapshot(new DOMParser().parseFromString(await r.text(), 'text/html'), sectionId); },
      async cart() { const r = await fetch(route('cart.js'), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } }); if (!r.ok) throw Error('Your cart could not be checked.'); return r.json(); },
      async add(item) { const r = await fetch(route('cart/add.js'), { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json', 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [item] }) }); let body = {}; try { body = await r.json(); } catch {} return { ok: r.ok, description: body.description }; },
      runtime() { return { currency: window.Shopify?.currency?.active, themeId: window.Shopify?.theme?.id }; }
    };
    const el = (tag, text, className) => { const node = doc.createElement(tag); if (text != null) node.textContent = text; if (className) node.className = className; return node; };
    function render(state) {
      root.querySelectorAll('[data-anb-form] button,[data-anb-form] input[name="quantity"],[data-anb-size]').forEach(control => {
        control.disabled = state.busy || state.locked || state.invalid || control.dataset.unavailable === 'true';
      });
      if (activeCard) {
        activeCard.querySelector('[data-anb-card-status]').textContent = state.message;
        activeCard.querySelector('[data-anb-cart-link]').hidden = state.busy || !state.message;
      } else {
        status.textContent = state.message;
      }
    }
    const controller = createDirectController({ snapshot, api, uid, onChange: render });
    root.querySelectorAll('[data-anb-card]').forEach(card => {
      const data = JSON.parse(card.querySelector('[data-anb-product-json]').textContent), picker = card.querySelector('[data-anb-size]');
      picker.hidden = false;
      function choose() { card.querySelectorAll('[data-anb-size-panel]').forEach(panel => { panel.hidden = panel.dataset.variantId !== picker.value; }); const link = new URL(data.url, location.origin); link.searchParams.set('variant', picker.value); const productLink = card.querySelector('[data-anb-product-link]'); if (productLink) productLink.href = previewLink(link.href, location.href, context); }
      picker.addEventListener('change', () => { choose(); if (!context.collectionId) { const url = new URL(location.href); url.searchParams.set('variant', picker.value); history.replaceState(null, '', url.href); } }); choose();
      card.querySelectorAll('[data-anb-form]').forEach(form => {
        form.addEventListener('submit', async event => {
          event.preventDefault();
          if (!form.reportValidity() || controller.state().busy || controller.state().locked || controller.state().invalid) return;
          activeCard = card;
          try {
            const result = await controller.addToCart(form.elements.id.value, form.elements.quantity.value);
            if (result.status === 'added') {
              const target = previewLink(route('cart'), location.href, context);
              if (api.goToCart) api.goToCart(target);
              else window.location.assign(target);
            }
          } catch (error) { card.querySelector('[data-anb-card-status]').textContent = error.message; card.querySelector('[data-anb-cart-link]').hidden = false; }
        });
      });
      card.querySelectorAll('[data-anb-artwork]').forEach(link => link.addEventListener('click', event => {
        event.preventDefault(); imageOpener = event.currentTarget;
        const image = artwork.querySelector('img'); image.src = event.currentTarget.href; image.alt = event.currentTarget.querySelector('img')?.alt || data.artwork.alt || data.name;
        artwork.querySelector('[data-anb-artwork-title]').textContent = data.name;
        const displayArtwork = data.use === 'display';
        artwork.querySelector('[data-anb-artwork-use]').textContent = displayArtwork ? artwork.dataset.displayLead : artwork.dataset.functionalLead;
        const useDetail = displayArtwork ? artwork.dataset.displayDetail : artwork.dataset.functionalDetail;
        artwork.querySelector('[data-anb-artwork-caption]').textContent = [data.caption, useDetail, data.detailNote].filter(Boolean).join(' ');
        artwork.showModal();
        artwork.querySelector('[data-anb-artwork-body]').scrollTop = 0;
        artwork.querySelector('button').focus();
      }));
    });
    const filters = root.querySelector('[data-anb-filters]');
    if (filters) {
      filters.hidden = false; const theme = filters.querySelector('[data-anb-theme]');
      [...new Set(snapshot.products.map(p => p.theme).filter(Boolean))].forEach(name => { const option = el('option', name); option.value = name; theme.append(option); });
      const apply = () => { const visible = new Set(filterProducts(snapshot.products, filters.querySelector('input').value, filters.querySelector('[data-anb-use]').value, theme.value).map(p => String(p.id))); root.querySelectorAll('[data-anb-card]').forEach(card => { card.hidden = !visible.has(card.dataset.productId); }); root.querySelector('[data-anb-results]').textContent = `${visible.size} of ${snapshot.products.length} designs on this page`; const empty = root.querySelector('[data-anb-empty]'); if (empty) empty.hidden = visible.size !== 0; };
      root.querySelector('[data-anb-reset]')?.addEventListener('click', () => { filters.querySelector('input').value = ''; filters.querySelector('[data-anb-use]').value = ''; theme.value = ''; apply(); filters.querySelector('input').focus(); });
      filters.addEventListener('input', apply); filters.addEventListener('change', apply); apply();
    }
    root.querySelectorAll('[data-anb-close]').forEach(button => button.addEventListener('click', () => button.closest('dialog').close()));
    artwork.addEventListener('close', () => returnFocus(imageOpener, root.querySelector('[data-anb-focus-fallback]') || root.querySelector('[data-anb-product-link]')));
    document.querySelectorAll('form[action*="localization"]').forEach(form => form.addEventListener('submit', controller.invalidate));
    window.addEventListener('pageshow', event => { if (event.persisted) controller.invalidate(); });
    root.querySelectorAll('a[href]').forEach(link => { link.href = previewLink(link.href, location.href, context); });
    root.dataset.enhanced = 'true'; render(controller.state());
    return controller;
  }
  return { allowedMarket, assertContext, validProduct, validVariant, count, signature, properties, recorded, filterProducts, createController, createDirectController, parseSnapshot, sourceURL, previewLink, trapDialogTab, returnFocus, mount };
});
